import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useState, useEffect, useRef, useMemo } from "react";
import NavBar from "../common/NavBar";
import Footer from "../common/Footer";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Tooltip } from 'react-tooltip';

const Booking = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const filteredApartments = useMemo(
    () => location.state?.filteredApartments || [],
    [location.state]
  );
  const [locations, setLocations] = useState({});
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [selectedApartment, setSelectedApartment] = useState(null);
  const [showPaypalButton, setShowPaypalButton] = useState(false);
  const [currencyData, setCurrencyData] = useState({ symbol: "€", code: "EUR" });
  const paypalButtonContainerRef = useRef(null);
  const [paypalLoading, setPaypalLoading] = useState(false);
  const tooltipMessage = t("please_login_to_make_reservation");
  const [currencyLoading, setCurrencyLoading] = useState({});

  const getApiBaseUrl = () => {
    return process.env.REACT_APP_API_URL || "http://localhost:8000";
  };

  // Get booking dates from location.state
  const startDate = location.state?.startDate;
  const endDate = location.state?.endDate;
  const adults = location.state?.adults || 1;
  const children = location.state?.children || 0;

  //house rules
  const [acceptedRules, setAcceptedRules] = useState(false);
  const lang = i18n.language && i18n.language.startsWith("de") ? "de" : "en";
  const houseRulesUrl = `/houserules/houserules_${lang}.pdf`;
  const [bookingCosts, setBookingCosts] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    setIsLoggedIn(!!token);
  }, []);

  useEffect(() => {
    const fetchBookingCosts = async () => {
      try {
        const response = await fetch(`${getApiBaseUrl()}/api/costs/booking-costs/public`);
        const data = await response.json();
        setBookingCosts(data);
      } catch (error) {
        console.error("Failed to fetch booking costs:", error);
      }
    };

    fetchBookingCosts();
  }, []);


  useEffect(() => {
    const fetchLocations = async () => {
      const locationData = {};
      for (const apartment of filteredApartments) {
        if (!apartment.location) continue;
        try {
          const response = await fetch(`${getApiBaseUrl()}/api/locations/${apartment.location}/`);
          const data = await response.json();
          locationData[apartment.location] = data.name;
        } catch (error) {
          console.error("Error fetching location:", error);
        }
      }
      setLocations(locationData);
    };

    fetchLocations();
  }, [filteredApartments]);

  const fetchCurrencyData = async (currencyId) => {
    if (!currencyId) {
      console.error(t('currency_id_not_provided'));
      return { symbol: "€", code: "EUR" };
    }

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/currencies/${currencyId}/`, {
        headers: {
          "Authorization": `Token ${localStorage.getItem("token")}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`${t('failed_to_fetch_currency')}. Status: ${response.status}`);
      }
      
      const data = await response.json();
      return {
        symbol: data.symbol || "€",
        code: data.code || "EUR",
        exchangeRate: parseFloat(data.exchange_rate_to_usd) || 1
      };
    } catch (error) {
      console.error(`${t('error_fetching_currency')}:`, error);
      return { symbol: "€", code: "EUR", exchangeRate: 1 };
    }
  };

  const fetchCurrencyForApartment = async (apartmentId) => {
    if (!startDate || !endDate) {
      console.error("Start or end date missing");
      return null;
    }

    try {
      const response = await fetch(
        `${getApiBaseUrl()}/api/properties/apartments/${apartmentId}/daily-prices/?start_date=${startDate}&end_date=${endDate}`,
        {
          headers: {
            "Authorization": `Token ${localStorage.getItem("token")}`
          }
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch daily prices");
      }

      const dailyPrices = await response.json();
      
      if (!dailyPrices.length) {
        throw new Error("No price data for selected dates");
      }

      const currencyIds = [...new Set(dailyPrices.map(price => price.currency))];
      
      if (currencyIds.length > 1) {
        throw new Error("Multiple currencies found for booking period");
      }

      return currencyIds[0];
    } catch (error) {
      console.error("Error fetching currency:", error);
      return null;
    }
  };

  const calculateFinalPrice = (apartment) => {
    let total = apartment.total_price || 0;

    bookingCosts.forEach(cost => {
      if (cost.apartment?.id !== apartment.id) return;

      const value = parseFloat(cost.value);

      if (cost.name.includes("Endreinigung")) {
        total += value;
      }

      if (cost.name.includes("Kurtax Kind")) {
        total += value * children;
      }

      if (cost.name.includes("Kurtax Erwachsene")) {
        total += value * adults;
      }
    });

    return total;
  };


  useEffect(() => {
    if (showPaypalButton && selectedApartment && window.paypal && paypalButtonContainerRef.current) {
      paypalButtonContainerRef.current.innerHTML = '';
      
      // Calculate amount in USD using exchange rate
      const price = calculateFinalPrice(selectedApartment);
      // amountUSD kept for reference; PayPal charges in EUR
      // const amountUSD = (price * (currencyData.exchangeRate || 1)).toFixed(2);
      
      if (price <= 0) {
        alert(t('invalid_payment_amount'));
        setShowPaypalButton(false);
        return;
      }

      window.paypal.Buttons({
        createOrder: (data, actions) => {
          return actions.order.create({
            purchase_units: [{
              amount: {
                value: price.toFixed(2),
                currency_code: 'EUR',
              },
              payee: {
                email_address: process.env.PAYPAL_EMAIL,
              },
            }],
          });
        },
        onApprove: (data, actions) => {
          return actions.order.capture().then(details => {
            alert(t('transaction_completed') + ' ' + details.payer.name.given_name);
            handleBook(selectedApartment);
          });
        },
        onError: (err) => {
          console.error('PayPal Checkout onError', err);
          alert(t('transaction_error'));
        },
      }).render(paypalButtonContainerRef.current);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPaypalButton, selectedApartment, t, currencyData]);

  const handleBook = async (apartment) => {
    const userId = localStorage.getItem("user_id");

    if (!userId) {
      alert(t("please_log_in"));
      return;
    }

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/bookings/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Token ${localStorage.getItem("token")}`
        },
        body: JSON.stringify({
          guest: parseInt(userId, 10),
          apartment: apartment.id,
          start_date: startDate,
          end_date: endDate,
        }),
      });

      if (!response.ok) {
        throw new Error(t("failed_to_book_apartment"));
      }

      alert(t("booking_successful"));
      navigate("/guest/dashboard");
    } catch (error) {
      console.error("Error booking apartment:", error);
      alert(t("failed_to_book_apartment"));
    }
  };

  const handlePaypalButtonClick = async (apartment) => {
    setPaypalLoading(true);
    setSelectedApartment(apartment);
    
    try {
      setCurrencyLoading(prev => ({...prev, [apartment.id]: true}));
      
      // First get currency ID from daily prices
      const currencyId = await fetchCurrencyForApartment(apartment.id);
      
      if (!currencyId) {
        alert(t("failed_to_determine_currency"));
        return;
      }

      // Then get full currency data (symbol, code, exchange rate)
      const currency = await fetchCurrencyData(currencyId);
      setCurrencyData(currency);
      
      // Validate price
      if (!apartment.total_price || apartment.total_price <= 0) {
        alert(t("invalid_apartment_price"));
        return;
      }
      
      setShowPaypalButton(true);
    } catch (error) {
      console.error("Error in payment process:", error);
      alert(t("payment_initialization_failed"));
    } finally {
      setPaypalLoading(false);
      setCurrencyLoading(prev => ({...prev, [apartment.id]: false}));
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      <div className="flex flex-col items-center justify-center mt-16">
        <h1 className="text-3xl font-bold mb-8">{t("booking")}</h1>

        {filteredApartments.length > 0 ? (
          <div className="w-full max-w-4xl mx-auto">
            <h2 className="text-2xl font-semibold mb-4 text-center">{t("available_apartments")}</h2>
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-200 text-center">
                  <th className="border border-gray-300 p-3 text-center">{t("apartment")}</th>
                  <th className="border border-gray-300 p-3 text-center">{t("location")}</th>
                  <th className="border border-gray-300 p-3 text-center">{t("total_booking_price")}</th>
                  <th className="border border-gray-300 p-3 text-center">{t("action")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredApartments.map((apartment) => (
                  <tr key={apartment.id} className="bg-white text-center">
                    <td className="border border-gray-300 p-3">
                      {isLoggedIn ? (
                        <Link
                          to={`/apartments/${apartment.id}`}
                          className="text-blue-600 hover:underline"
                        >
                          {apartment.apartment_name}
                        </Link>
                      ) : (
                        <span className="text-gray-400 cursor-not-allowed" title={t("please_log_in")}>
                          {apartment.apartment_name}
                        </span>
                      )}
                    </td>
                    <td className="border border-gray-300 p-3">
                      {locations[apartment.location] || t("loading")}
                    </td>
                    <td className="border border-gray-300 p-3">
                      {currencyLoading[apartment.id] ? (
                        <span className="text-gray-500">{t("loading")}</span>
                      ) : (
                        `${currencyData.symbol}${calculateFinalPrice(apartment).toFixed(2) ?? t("loading")}`
                      )}
                    </td>
                    <td className="border border-gray-300 p-3 space-x-2">
                      {isLoggedIn && (
                        <div className="my-2">
                          <a
                            href={houseRulesUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 underline mb-2 block"
                          >
                            {t("view_house_rules")}
                          </a>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={acceptedRules}
                              onChange={e => setAcceptedRules(e.target.checked)}
                              className="mr-2"
                            />
                            {t("i_accept_house_rules")}
                          </label>
                        </div>
                      )}
                      <button
                        {...(!isLoggedIn && {
                          "data-tooltip-id": "login-tooltip",
                          "data-tooltip-content": tooltipMessage,
                        })}
                        onClick={() => handlePaypalButtonClick(apartment)}
                        disabled={!isLoggedIn || paypalLoading || (isLoggedIn && !acceptedRules) || currencyLoading[apartment.id]}
                        className={`bg-blue-500 text-white px-4 py-2 rounded-md ${
                          isLoggedIn && !paypalLoading && acceptedRules && !currencyLoading[apartment.id] 
                            ? "hover:bg-blue-600" 
                            : "opacity-50 cursor-not-allowed"
                        } flex items-center justify-center`}
                      >
                        {paypalLoading || currencyLoading[apartment.id] ? (
                          <>
                            <span className="spinner"></span>
                            {t("loading")}
                          </>
                        ) : (
                          t("pay_with_paypal")
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {showPaypalButton && selectedApartment && (
              <div className="mt-8 p-4 bg-gray-100 rounded-lg">
                <h3 className="text-lg font-semibold mb-4">
                  {t("paying_for")}: {selectedApartment.apartment_name}
                </h3>
                <p className="text-lg mb-4">
                  {t("total_price")}: {currencyData.symbol}{calculateFinalPrice(selectedApartment).toFixed(2)}
                  <br />
                  ({t("converted_to")} USD: ${(calculateFinalPrice(selectedApartment) * (currencyData.exchangeRate || 1)).toFixed(2)})
                </p>
                <div
                  id="paypal-button-container"
                  ref={paypalButtonContainerRef}
                  className="w-full"
                  style={{ minHeight: '40px' }}
                ></div>
                <button
                  onClick={() => setShowPaypalButton(false)}
                  className="mt-4 bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
                >
                  {t("cancel")}
                </button>
              </div>
            )}
          </div>
        ) : (
          <p className="text-gray-600">{t("no_apartments_found")}</p>
        )}
      </div>
      <Footer />
      {!isLoggedIn && (
        <Tooltip
          id="login-tooltip"
          place="top"
          effect="solid"
          className="max-w-xs z-50"
        />
      )}
    </div>
  );
};

export default Booking;